<script setup lang="ts">
  import { computed, inject, ref } from 'vue';
  // Components
  import {
    IconButton,
    Tooltip,
    IconImage,
    Popover,
    PopoverContent,
    PopoverTrigger,
  } from '@/shared/ui';
  // Types
  import { isExternalDocument, type DocumentRevision } from '@/entities/Document';
  // Icons
  import HistoryIcon from '@/shared/assets/icons/HistoryIcon.svg';
  import CertificateIcon from '@/shared/assets/icons/CertificateIcon.svg';
  import SettingsIcon from '@/shared/assets/icons/SettingsIcon.svg';
  import EyeIcon from '@/shared/assets/icons/EyeActionIcon.svg';
  import EllipsisIcon from '@/shared/assets/icons/EllipsisIcon.svg';
  import PencilIcon from '@/shared/assets/icons/PencilBoldIcon.svg';
  import VectorIcon from '@/shared/assets/icons/VectorIcon.svg';
  import TrashIcon from '@/shared/assets/icons/TrashBoldIcon.svg';
  import RefreshIcon from '@/shared/assets/icons/Refresh.svg';
  // Composables
  import { useDocumentActions } from '..';
  import { useDocuments } from '@/features/Document';
  import { hasCompletedHomologation } from '@/shared/lib';
  import { RefreshDocumentListKey } from '@/shared/lib/sortOrder';

  const props = defineProps<{
    revision: DocumentRevision;
  }>();

  const emit = defineEmits<{
    delete: [id: number];
    replace: [revision: DocumentRevision];
  }>();

  const refreshDocumentList = inject(RefreshDocumentListKey);
  const { openModal, isLoadingEditInternal } = useDocumentActions(refreshDocumentList);
  const { viewDocument } = useDocuments();

  const isExternal = computed(() => isExternalDocument(props.revision));
  const showEditHomologation = computed(() => {
    if (isExternal.value) return false;
    return hasCompletedHomologation(props.revision.status);
  });
  const isMoreDropdownOpen = ref(false);

  const handleReplaceDocument = () => {
    isMoreDropdownOpen.value = false;
    emit('replace', props.revision);
  };
  const MENU_ITEM_CLASS =
    'mt-1 flex w-full items-center gap-3 whitespace-nowrap rounded-lg px-3 py-2 text-left text-sm text-gray-07 first:mt-0 hover:bg-gray-01';
  const MENU_ITEM_ICON_CLASS = 'w-6 h-6 shrink-0 text-gray-xx-02';
</script>

<template>
  <td class="px-6 py-4.5 text-sm text-gray-07">
    <div v-if="isExternal" class="flex gap-2 items-center text-gray-xx-02">
      <Tooltip content="Visualizar" placement="top" class="flex items-center">
        <IconButton
          :src="EyeIcon"
          alt="Link"
          class="w-6 h-6"
          @click="viewDocument(revision.documentId.toString(), undefined, revision.id)"
        />
      </Tooltip>
      <Tooltip content="Editar" placement="top" class="flex items-center">
        <IconButton
          :src="PencilIcon"
          alt="Link"
          class="w-6 h-6"
          @click="openModal(revision, 'editExternal', { title: 'Editar/Configurar Documento' })"
        />
      </Tooltip>
      <Tooltip content="Substituir" placement="top" class="flex items-center">
        <IconButton :src="RefreshIcon" alt="Link" @click="handleReplaceDocument" />
      </Tooltip>
      <Tooltip content="Deletar" placement="top" class="flex items-center">
        <IconButton
          :src="TrashIcon"
          alt="Link"
          class="w-6 h-6"
          @click="emit('delete', revision.id)"
        />
      </Tooltip>
    </div>
    <div v-else class="flex gap-2 items-center text-gray-xx-02">
      <Tooltip content="Histórico" placement="top" class="flex items-center">
        <IconButton
          :src="HistoryIcon"
          alt="History"
          @click="openModal(revision, 'history', { title: 'Histórico de documentos' })"
        />
      </Tooltip>
      <Tooltip content="Treinamento" placement="top" class="flex items-center">
        <IconButton
          :src="CertificateIcon"
          alt="Certificate"
          @click="openModal(revision, 'training', { title: 'Controle de Treinamento' })"
        />
      </Tooltip>
      <Tooltip content="Controle" placement="top" class="flex items-center">
        <IconButton
          :src="SettingsIcon"
          alt="Settings"
          @click="openModal(revision, 'control', { title: 'Controle de documentos' })"
        />
      </Tooltip>
      <Tooltip content="Visualizar" placement="top" class="flex items-center">
        <IconButton
          :src="EyeIcon"
          alt="Link"
          @click="viewDocument(revision.documentId.toString(), undefined, revision.id)"
        />
      </Tooltip>
      <Tooltip content="Mais" placement="top" class="flex items-center">
        <Popover v-model:open="isMoreDropdownOpen">
          <PopoverTrigger as-child>
            <IconButton :src="EllipsisIcon" alt="More" />
          </PopoverTrigger>
          <PopoverContent
            data-testid="document-more-actions-menu"
            side="bottom"
            align="end"
            class="w-auto min-w-49 rounded-2xl border-0 bg-white-01 px-3 py-2 shadow-2xl"
          >
            <button
              :class="[MENU_ITEM_CLASS, 'disabled:opacity-50 disabled:cursor-not-allowed']"
              :disabled="isLoadingEditInternal"
              @click="openModal(revision, 'editInternal', { title: 'Editar/Configurar Documento' })"
            >
              <IconImage :src="PencilIcon" alt="Pencil" :class="MENU_ITEM_ICON_CLASS" />
              {{ isLoadingEditInternal ? 'Carregando...' : 'Editar/Configurar' }}
            </button>
            <button
              :class="MENU_ITEM_CLASS"
              @click="handleReplaceDocument"
            >
              <IconImage
                :src="RefreshIcon"
                alt="Refresh"
                class="whitespace-nowrap flex flex-shrink-0 w-6 h-6 text-gray-xx-02"
              />
              Substituir Documento
            </button>
            <button
              v-if="showEditHomologation"
              :class="MENU_ITEM_CLASS"
              @click="openModal(revision, 'editHomologation', { title: 'Editar Homologação' })"
            >
              <IconImage :src="VectorIcon" alt="Vector" :class="MENU_ITEM_ICON_CLASS" />
              Editar Homologação
            </button>
            <button
              :class="MENU_ITEM_CLASS"
              @click="emit('delete', revision.id)"
            >
              <IconImage :src="TrashIcon" alt="Trash" :class="MENU_ITEM_ICON_CLASS" />
              Deletar
            </button>
          </PopoverContent>
        </Popover>
      </Tooltip>
    </div>
  </td>
</template>
